// @ts-check

/** @file Lexical image node for uploaded-file-backed editor images. */

import {$applyNodeReplacement, createCommand, DecoratorNode} from "lexical"

import {pendingUploadAttribute, uploadedFileAttribute} from "./upload-session"

/** @import {EditorConfig, LexicalCommand, LexicalNode, NodeKey, SerializedLexicalNode} from "lexical" */
/** @import {ReactElement} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{altText?: string, pendingUploadId?: string, src: string, uploadedFileId?: string}} ImageNodePayload */
/** @typedef {SerializedLexicalNode & ImageNodePayload & {version: 1}} SerializedImageNode */

/** @type {LexicalCommand<ImageNodePayload>} */
export const INSERT_IMAGE_COMMAND = createCommand("INSERT_IMAGE_COMMAND")

/** Lexical node for images embedded in WYSIWYG fields. */
export class ImageNode extends DecoratorNode {
  /** @type {string} */
  __altText
  /** @type {string | undefined} */
  __pendingUploadId
  /** @type {string} */
  __src
  /** @type {string | undefined} */
  __uploadedFileId

  /**
   * Stores editor image metadata.
   * @param {ImageNodePayload} payload Source URL and upload metadata.
   * @param {NodeKey} [key] Existing Lexical node key when cloning.
   */
  constructor({altText = "", pendingUploadId, src, uploadedFileId}, key) {
    super(key)
    this.__altText = altText
    this.__pendingUploadId = pendingUploadId
    this.__src = src
    this.__uploadedFileId = uploadedFileId
  }

  /**
   * Identifies this node in serialized Lexical state.
   * @returns {string} Lexical node type.
   */
  static getType() {
    return "image"
  }

  /**
   * Clones an image node.
   * @param {ImageNode} node Existing node.
   * @returns {ImageNode} Cloned node.
   */
  static clone(node) {
    return new ImageNode({
      altText: node.__altText,
      pendingUploadId: node.__pendingUploadId,
      src: node.__src,
      uploadedFileId: node.__uploadedFileId
    }, node.__key)
  }

  /**
   * Imports serialized image state.
   * @param {SerializedImageNode} serializedNode Payload produced by exportJSON.
   * @returns {ImageNode} Imported image node.
   */
  static importJSON(serializedNode) {
    return $createImageNode({
      altText: serializedNode.altText,
      pendingUploadId: serializedNode.pendingUploadId,
      src: serializedNode.src,
      uploadedFileId: serializedNode.uploadedFileId
    })
  }

  /**
   * Imports regular HTML image tags.
   * @returns {Record<string, () => {conversion: (element: HTMLElement) => {node: LexicalNode | null}, priority: 0}>} DOM conversion map.
   */
  static importDOM() {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0
      })
    }
  }

  /**
   * Exports serialized image state.
   * @returns {SerializedImageNode} Serialized node.
   */
  exportJSON() {
    return {
      altText: this.__altText,
      pendingUploadId: this.__pendingUploadId,
      src: this.__src,
      type: "image",
      uploadedFileId: this.__uploadedFileId,
      version: 1
    }
  }

  /**
   * Creates the wrapper DOM node.
   * @returns {HTMLElement} Wrapper element.
   */
  createDOM() {
    return document.createElement("span")
  }

  /**
   * Keeps the same wrapper DOM node.
   * @returns {false} Lexical should not replace the wrapper.
   */
  updateDOM() {
    return false
  }

  /**
   * Exports the image as an HTML `&lt;img&gt;` element.
   * @returns {{element: HTMLImageElement}} Exported DOM node.
   */
  exportDOM() {
    const element = document.createElement("img")

    element.setAttribute("alt", this.__altText)
    element.setAttribute("src", this.__src)
    if (this.__pendingUploadId) {
      element.setAttribute(pendingUploadAttribute, this.__pendingUploadId)
    }
    if (this.__uploadedFileId) {
      element.setAttribute(uploadedFileAttribute, this.__uploadedFileId)
    }

    return {element}
  }

  /**
   * Renders the image inside the editor.
   * @returns {ReactElement} Rendered image.
   */
  decorate() {
    return (
      <img
        alt={this.__altText}
        data-pending-upload-id={this.__pendingUploadId}
        data-uploaded-file-id={this.__uploadedFileId}
        src={this.__src}
        style={styles.image ||= {
          borderRadius: 6,
          display: "block",
          height: "auto",
          marginBottom: 8,
          maxWidth: "100%"
        }}
      />
    )
  }
}

/**
 * Creates an image node.
 * @param {ImageNodePayload} payload Source URL and upload metadata.
 * @returns {ImageNode} Replacement image node.
 */
export function $createImageNode(payload) {
  return $applyNodeReplacement(new ImageNode(payload))
}

/**
 * Converts an HTML image element to a Lexical image node.
 * @param {HTMLElement} element DOM element.
 * @returns {{node: LexicalNode | null}} Converted node.
 */
function convertImageElement(element) {
  const imageElement = /** @type {HTMLImageElement} */ (element)
  const src = imageElement.getAttribute("src") || ""

  if (!src) {
    return {node: null}
  }

  return {
    node: $createImageNode({
      altText: imageElement.getAttribute("alt") || "",
      pendingUploadId: imageElement.getAttribute(pendingUploadAttribute) || undefined,
      src,
      uploadedFileId: imageElement.getAttribute(uploadedFileAttribute) || undefined
    })
  }
}
